import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Facebook, Instagram, MessageCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { getCategories, subscribeNewsletter } from "../lib/api";

const DEFAULT_FOOTER_CATEGORIES = [
  { slug: "hombre", name: "Hombre" },
  { slug: "mujer", name: "Mujer" },
  { slug: "accesorios", name: "Accesorios" },
];

const Footer = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [newsletterForm, setNewsletterForm] = useState({
    email: "",
    whatsappPhone: "",
  });
  const [footerCategories, setFooterCategories] = useState(DEFAULT_FOOTER_CATEGORIES);

  useEffect(() => {
    let isMounted = true;
    getCategories()
      .then((data) => {
        if (!isMounted) return;
        if (!Array.isArray(data)) return;
        const mapped = data
          .filter((item) => item && item.slug && item.name)
          .map((item) => ({ slug: item.slug, name: item.name }));
        setFooterCategories(mapped);
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  const handleNewsletter = async (event) => {
    event.preventDefault();
    const email = newsletterForm.email.trim();
    const whatsappPhone = newsletterForm.whatsappPhone.trim();
    if (!email || !whatsappPhone) {
      toast({
        title: "Completa tus datos",
        description: "Debes ingresar correo y WhatsApp.",
        variant: "destructive",
      });
      return;
    }
    setSendingNewsletter(true);
    try {
      const response = await subscribeNewsletter({
        email,
        whatsapp_phone: whatsappPhone,
      });
      setNewsletterForm({ email: "", whatsappPhone: "" });
      navigate("/newsletter/registro-exitoso", {
        state: {
          couponCode: response?.coupon_code || "",
          couponExpiresAt: response?.coupon_expires_at || "",
          couponCreated: Boolean(response?.coupon_created),
        },
      });
    } catch (error) {
      toast({
        title: "No se pudo registrar",
        description:
          error.response?.data?.detail || "Intenta nuevamente en unos segundos.",
        variant: "destructive",
      });
    } finally {
      setSendingNewsletter(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white mt-20">
      <div className="bg-gray-800 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-2">Suscribete a nuestras novedades</h3>
            <p className="text-gray-400 mb-6">
              Se parte de la familia Eclipse Tenis y recibe un cupon del 5% para tu proxima compra.
            </p>
            <form onSubmit={handleNewsletter} className="grid gap-2 max-w-md mx-auto">
              <Input
                type="email"
                name="newsletter-email"
                placeholder="tu@email.com"
                required
                className="bg-white text-black"
                value={newsletterForm.email}
                onChange={(event) =>
                  setNewsletterForm((prev) => ({ ...prev, email: event.target.value }))
                }
              />
              <Input
                type="tel"
                name="newsletter-whatsapp"
                placeholder="WhatsApp (ej: 3184215616)"
                required
                className="bg-white text-black"
                value={newsletterForm.whatsappPhone}
                onChange={(event) =>
                  setNewsletterForm((prev) => ({
                    ...prev,
                    whatsappPhone: event.target.value,
                  }))
                }
              />
              <Button
                type="submit"
                className="bg-white text-black hover:bg-gray-200"
                disabled={sendingNewsletter}
              >
                {sendingNewsletter ? "Registrando..." : "Suscribirse"}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h4 className="text-lg font-bold mb-4">Eclipse Tenis</h4>
            <p className="text-gray-400 text-sm">
              Tu tienda de confianza para zapatillas de calidad en Colombia.
              Estilo, comodidad y las mejores marcas.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Enlaces</h4>
            <ul className="space-y-2 text-sm">
              {footerCategories.map((category) => (
                <li key={category.slug}>
                  <Link
                    to={`/catalogo/${category.slug}`}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/ofertas"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Ofertas
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Atencion al Cliente</h4>
            <ul className="space-y-2 text-sm">
              <li className="text-gray-400">Atencion 24/7 todos los dias.</li>
              <li className="text-gray-400">eclipseestilo20.19@gmail.com</li>
              <li>
                <Link
                  to="/terminos"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Terminos y Condiciones
                </Link>
              </li>
              <li>
                <Link
                  to="/privacidad"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Politica de Privacidad
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Siguenos</h4>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/eclipse.tenis?igsh=c3g4M2VhcHVvanZv"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Instagram className="h-6 w-6" />
              </a>
              <a
                href="https://www.facebook.com/share/171VAJCLAK/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Facebook className="h-6 w-6" />
              </a>
              <a
                href="https://wa.me/573184215616"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <MessageCircle className="h-6 w-6" />
              </a>
            </div>
            <p className="text-sm text-gray-400 mt-4">WhatsApp: +57 318 421 5616</p>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
          <p>&copy; 2026 Eclipse Tenis. Todos los derechos reservados.</p>
          <p className="mt-2">
            Cumplimiento Ley 1581/2012 - Decreto 1377/2013 |{" "}
            <a
              href="https://www.sic.gov.co"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              SIC
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
